import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { formatBRDateTime } from '@control-center/shared';

import { fetchBootstrapRun } from '../../lib/bootstrap-api';

export function BootstrapRunDetailPage(): JSX.Element {
  const { runId = '' } = useParams<{ runId: string }>();
  const query = useQuery({
    queryKey: ['bootstrap-run', runId],
    queryFn: () => fetchBootstrapRun(runId),
    refetchInterval: 5_000,
  });

  if (query.isLoading) {
    return <div className="mx-auto max-w-3xl px-6 py-6 text-sm text-muted">Carregando…</div>;
  }
  if (!query.data) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-6 text-sm text-muted">Run não encontrada.</div>
    );
  }

  const run = query.data;
  const statusColor: Record<typeof run.status, string> = {
    queued: 'bg-panel-2 text-muted',
    running: 'bg-brand/10 text-brand',
    paused: 'bg-warn/10 text-warn',
    completed: 'bg-good/10 text-good',
    failed: 'bg-bad/10 text-bad',
    cancelled: 'bg-muted/10 text-muted',
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-6 py-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">
            Bootstrap — {run.saasProduct.name}
          </h1>
          <p className="mt-1 text-sm text-muted">
            Iniciado em {formatBRDateTime(run.initiatedAt)} · {run.isDryRun ? 'dry-run' : 'real'}
          </p>
        </div>
        <span
          className={`rounded-[6px] px-3 py-1 text-[11px] font-medium uppercase tracking-wider ${statusColor[run.status]}`}
        >
          {run.status}
        </span>
      </header>

      <section className="rounded-[12px] border border-line bg-panel p-5">
        <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          Progresso
        </h2>
        <pre className="overflow-x-auto rounded-[8px] border border-line bg-panel-2 p-3 text-[11px] text-ink">
          {JSON.stringify(run.progress, null, 2)}
        </pre>
      </section>

      {run.report ? (
        <section className="rounded-[12px] border border-line bg-panel p-5">
          <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
            Report
          </h2>
          <pre className="overflow-x-auto rounded-[8px] border border-line bg-panel-2 p-3 text-[11px] text-ink">
            {JSON.stringify(run.report, null, 2)}
          </pre>
        </section>
      ) : null}

      {run.errorMessage && (
        <section className="rounded-[12px] border border-bad/40 bg-bad/10 p-4 text-[12px] text-ink">
          <strong>Erro:</strong> {String(run.errorMessage)}
        </section>
      )}
    </div>
  );
}
